<template>
  <div>
    <page-toolbar v-if="!device.isMobile" :searchable="true" v-model:search="search">
      <template #filters>
        <date-picker
          v-model="datePickerValue"
          color="white"
          :single-date="activeTab === 2"
          style="min-width: 280px; flex-shrink: 0"
        />

        <autocomplete
          v-if="activeTab !== 8"
          :label="$t('restaurants')"
          v-model="selectedRestaurants"
          :items="restaurants"
          :item-text="(r) => toLatin(r.name)"
          item-value="id"
          multiple
          hide-details
          color="white"
          show-selection-slot
          style="width: 300px; min-width: 300px; flex: 0 0 300px"
        >
          <template #selection="{ data }">
            <span v-if="data.index === 0" class="text-truncate">{{ restaurantSummary }}</span>
          </template>
        </autocomplete>

        <autocomplete
          v-if="activeTab === 8"
          :label="$t('restaurant')"
          v-model="selectedRestaurant"
          :items="restaurants"
          :item-text="(r) => toLatin(r.name)"
          item-value="id"
          hide-details
          color="white"
          style="width: 300px; min-width: 300px; flex: 0 0 300px"
        />

        <autocomplete
          v-if="showBranches"
          :label="$t('branches')"
          v-model="selectedBranches"
          :items="reportBranches"
          :item-text="(b) => toLatin(b.name)"
          item-value="id"
          multiple
          hide-details
          color="white"
          show-selection-slot
          style="width: 200px; min-width: 200px; flex: 0 0 200px"
        >
          <template #selection="{ data }">
            <span v-if="data.index === 0" class="text-truncate">{{ branchSummary }}</span>
          </template>
        </autocomplete>

        <autocomplete
          v-if="activeTab === 3"
          :label="$t('order_status')"
          v-model="employeeStatus"
          :items="employeeStatusItems"
          item-text="text"
          item-value="value"
          hide-details
          color="white"
          style="width: 220px; min-width: 220px; flex: 0 0 220px"
        />

        <shift-picker
          v-if="![4, 5, 6, 8].includes(activeTab)"
          v-model="selectedShifts"
          :shifts="shifts"
          multiple
          :label="$t('shifts')"
          item-text="company"
          color="white"
          style="flex-shrink: 0"
        />

        <autocomplete
          v-if="activeTab === 6"
          v-model="topupSource"
          :items="topupSources"
          :label="$t('topup_source')"
          item-text="text"
          item-value="value"
          clear
          chips
          small-chips
          color="white"
          multiple
          style="min-width: 200px; flex-shrink: 0"
        />

        <autocomplete
          v-if="activeTab === 6"
          v-model="paymentMethod"
          :items="paymentMethods"
          :label="$t('payment_method')"
          item-text="text"
          item-value="value"
          clear
          chips
          small-chips
          color="white"
          multiple
          style="min-width: 200px; flex-shrink: 0"
        />
      </template>
    </page-toolbar>

    <template v-else>
      <mobile-filters
        :filters="mobileFilterConfig"
        @update:filter="handleMobileFilterUpdate"
        @apply="onFiltersApply"
        @reset="resetFilters"
      />
    </template>

    <div :class="device.isMobile ? 'px-0 mt-2' : 'px-2 mt-2'">
      <v-tabs v-model="activeTab" background-color="primary" dark>
        <v-tab v-for="item in tabItems" :key="item.value" :value="item.value">
          <v-icon style="margin-right: 7px">{{ item.icon }}</v-icon>
          {{ item.tab }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" :touchless="true">
        <v-tab-item v-for="item in tabItems" :key="item.value">
          <component
            v-if="activeTab === item.value"
            :is="item.component"
            :preselected-user-id="item.value === 5 ? preselectedUserId : null"
            @navigate-to-topup="handleNavigateToTopup"
          ></component>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <!-- Mobile FAB Menu -->
    <v-speed-dial
      v-if="device.isMobile"
      v-model="fabMenu"
      fixed
      right
      direction="bottom"
      transition="slide-y-reverse-transition"
      style="top: 80px"
    >
      <template v-slot:activator>
        <v-btn v-model="fabMenu" color="grey" fab dark small depressed>
          <v-icon v-if="fabMenu">mdi-close</v-icon>
          <v-icon v-else>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>

      <!-- Excel Button -->
      <v-btn fab small color="green darken-2" dark @click="setExcel()">
        <v-icon>mdi-microsoft-excel</v-icon>
      </v-btn>

      <!-- Print Button -->
      <v-btn fab small color="primary" dark @click="setPrinter()">
        <v-icon>mdi-printer</v-icon>
      </v-btn>
    </v-speed-dial>
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import hasTopupFilters from "@/mixins/report/hasTopupFilters";
import hasCompanyBranches from "@/mixins/report/hasCompanyBranches";
import MobileFilters from "@/components/global/MobileFilters.vue";
import {
  CompanyMultiRestaurantTabRestaurantMeals,
  CompanyMultiRestaurantTabPackages,
  CompanyMultiRestaurantTabMeals,
  CompanyMultiRestaurantTabEmployees,
  CompanyMultiRestaurantTabCreditUsers,
  CompanyMultiRestaurantTabTopup,
  CompanyMultiRestaurantTabRestaurantSummary,
  CompanyMultiRestaurantTabCreditTopups,
  CompanyMultiRestaurantTabEmployeeMealSummary,
} from "./multi-restaurant-tabs";

export default {
  name: "CompanyMultiRestaurantReport",
  mixins: [hasSnackbar, hasTopupFilters, hasCompanyBranches],
  components: {
    MobileFilters,
  },
  data() {
    return {
      shifts: [1, 2, 3, 4],
      activeTab: 0,
      fabMenu: false,
      preselectedUserId: null,
    };
  },
  mounted() {
    // Status radnika počinje od svih (izbor je u store-u, pa ga stranica ne nasleđuje iz novog izgleda)
    this.employeeStatus = 0;

    // Set all restaurants as selected by default
    if (this.restaurants.length > 0 && this.selectedRestaurants.length === 0) {
      this.selectedRestaurants = this.restaurants.map((r) => r.id);
    }

    // Set first restaurant as selected for single restaurant filter
    if (this.restaurants.length > 0 && !this.selectedRestaurant) {
      this.selectedRestaurant = this.restaurants[0].id;
    }
  },
  beforeDestroy() {},
  watch: {
    activeTab() {},
    restaurants: {
      handler(newRestaurants) {
        // Auto-select first restaurant if none selected
        if (newRestaurants.length > 0 && !this.selectedRestaurant) {
          this.selectedRestaurant = newRestaurants[0].id;
        }
      },
      immediate: true,
    },
  },
  computed: {
    restaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    tabItems() {
      return [
        {
          tab: this.$t("meals"),
          icon: "mdi-silverware-fork-knife",
          component: CompanyMultiRestaurantTabMeals,
          value: 0,
        },
        {
          tab: this.$t("restaurant_meals"),
          icon: "mdi-package-variant",
          component: CompanyMultiRestaurantTabRestaurantMeals,
          value: 1,
        },
        {
          tab: this.$t("pak"),
          icon: "mdi-shopping-outline",
          component: CompanyMultiRestaurantTabPackages,
          value: 2,
        },
        {
          tab: this.$t("employees"),
          icon: "mdi-account",
          component: CompanyMultiRestaurantTabEmployees,
          value: 3,
        },
        {
          tab: this.$t("credit_users"),
          icon: "mdi-account",
          component: CompanyMultiRestaurantTabCreditUsers,
          value: 4,
        },
        {
          tab: this.$t("add_credit"),
          icon: "mdi-cash-plus",
          component: CompanyMultiRestaurantTabTopup,
          value: 5,
        },
        {
          tab: this.$t("credit_topups"),
          icon: "mdi-credit-card-check",
          component: CompanyMultiRestaurantTabCreditTopups,
          value: 6,
        },
        {
          tab: this.$t("restaurant_summary"),
          icon: "mdi-chart-bar",
          component: CompanyMultiRestaurantTabRestaurantSummary,
          value: 7,
        },
        {
          tab: this.$t("employee_meal_summary"),
          icon: "mdi-account-cash",
          component: CompanyMultiRestaurantTabEmployeeMealSummary,
          value: 8,
        },
      ];
    },
    mobileFilterConfig() {
      const filters = {
        dates: {
          type: "date",
          label: this.$t("date"),
          icon: "mdi-calendar",
          value: this.dates,
          chipText:
            this.dates.length === 2
              ? `${this.localeDate(this.dates[0])} - ${this.localeDate(this.dates[1])}`
              : this.$t("select_date"),
          visible: true,
          clearable: false,
        },
        restaurants: {
          type: "autocomplete",
          label: this.activeTab === 8 ? this.$t("restaurant") : this.$t("restaurants"),
          icon: "mdi-store",
          items: this.restaurants,
          itemText: "name",
          itemValue: "id",
          value:
            this.activeTab === 8 ? this.selectedRestaurant : this.selectedRestaurants,
          multiple: this.activeTab !== 8,
          chipText:
            this.activeTab === 8
              ? this.restaurants.find((r) => r.id === this.selectedRestaurant)?.name ||
                this.$t("select_restaurant")
              : this.selectedRestaurants.length > 0
              ? `${this.selectedRestaurants.length} ${this.$t("restaurants")}`
              : this.$t("all_restaurants"),
          visible: true,
          clearable: true,
        },
        search: {
          type: "search",
          label: this.$t("search"),
          icon: "mdi-magnify",
          value: this.search,
          chipText: this.search || this.$t("search"),
          visible: !!this.search,
          clearable: true,
        },
      };

      if (this.showBranches) {
        filters.branches = {
          type: "autocomplete",
          label: this.$t("branches"),
          icon: "mdi-source-branch",
          items: this.reportBranches,
          itemText: "name",
          itemValue: "id",
          value: this.selectedBranches,
          multiple: true,
          chipText:
            this.selectedBranches.length === this.reportBranches.length
              ? this.$t("branches")
              : `${this.selectedBranches.length}/${this.reportBranches.length} ${this.$t("branches")}`,
          visible: true,
          clearable: false,
        };
      }

      if (this.activeTab !== 4 && this.activeTab !== 5 && this.activeTab !== 8) {
        filters.shifts = {
          type: "autocomplete",
          label: this.$t("shifts"),
          icon: "mdi-clock-outline",
          items: this.shifts,
          itemText: "company",
          itemValue: "id",
          value: this.selectedShifts,
          multiple: true,
          chipText:
            this.selectedShifts.length > 0
              ? `${this.selectedShifts.length} ${this.$t("shifts")}`
              : this.$t("all_shifts"),
          visible: true,
          clearable: true,
        };
      }

      if (this.activeTab === 6) {
        filters.topupSource = {
          type: "autocomplete",
          label: this.$t("topup_source"),
          icon: "mdi-source-branch",
          items: this.topupSources,
          itemText: "text",
          itemValue: "value",
          value: this.topupSource,
          multiple: true,
          chipText:
            this.topupSource.length > 0
              ? this.topupSource.join(", ")
              : this.$t("all_sources"),
          visible: true,
          clearable: true,
        };

        filters.paymentMethod = {
          type: "autocomplete",
          label: this.$t("payment_method"),
          icon: "mdi-credit-card",
          items: this.paymentMethods,
          itemText: "text",
          itemValue: "value",
          value: this.paymentMethod,
          multiple: true,
          chipText:
            this.paymentMethod.length > 0
              ? `${this.paymentMethod.length} ${this.$t("payment_methods")}`
              : this.$t("all_payment_methods"),
          visible: true,
          clearable: true,
        };
      }

      return filters;
    },
    /** Izbor poslovnice važi za sve izveštaje osim unosa kredita (prazan izbor u store-u = sve poslovnice). */
    showBranches() {
      return this.hasBranches && this.activeTab !== 5;
    },
    branchSummary() {
      return this.pickedSummary(this.reportBranches, this.selectedBranchIds);
    },
    restaurantSummary() {
      return this.pickedSummary(this.restaurants, this.selectedRestaurants);
    },
    selectedBranches: {
      get() {
        return this.selectedBranchIds;
      },
      set(ids) {
        this.$store.commit(
          "report/SET_SELECTED_BRANCHES",
          ids.length === this.reportBranches.length ? [] : ids
        );
      },
    },
    employeeStatus: {
      get() {
        return this.$store.state.report.filters.employeeStatus || 0;
      },
      set(value) {
        this.$store.commit("report/SET_FILTER", { key: "employeeStatus", value });
      },
    },
    employeeStatusItems() {
      return [
        { text: this.$t("all"), value: 0 },
        { text: this.$t("users_ordered"), value: 1 },
        { text: this.$t("not_ordered"), value: 2 },
        { text: this.$t("users_ordered_taken"), value: 3 },
        { text: this.$t("users_ordered_not_taken"), value: 4 },
      ];
    },
    selectedRestaurants: {
      get() {
        return this.$store.state.report.selectedRestaurants || [];
      },
      set(value) {
        this.$store.commit("report/SET_SELECTED_RESTAURANTS", value);
      },
    },
    selectedRestaurant: {
      get() {
        return this.$store.state.report.selectedRestaurant;
      },
      set(value) {
        this.$store.commit("report/SET_SELECTED_RESTAURANT", value);
      },
    },
    dates: {
      get() {
        const { from, to } = this.$store.state.report.filters.dates;
        return [from, to];
      },
      set(value) {
        if (Array.isArray(value) && value.length === 2) {
          this.$store.commit("report/SET_DATE_RANGE", {
            from: value[0],
            to: value[1],
          });
        }
      },
    },
    datePickerValue: {
      get() {
        const { from, to } = this.$store.state.report.filters.dates;
        // If Packages tab (activeTab === 2), return single date
        if (this.activeTab === 2) {
          return from;
        }
        // Otherwise return array for date range
        return [from, to];
      },
      set(value) {
        // If single date (string), set both from and to to same value
        if (typeof value === "string") {
          this.$store.commit("report/SET_DATE_RANGE", {
            from: value,
            to: value,
          });
        }
        // If array, set from and to
        else if (Array.isArray(value) && value.length === 2) {
          this.$store.commit("report/SET_DATE_RANGE", {
            from: value[0],
            to: value[1],
          });
        }
      },
    },
    selectedShifts: {
      get() {
        return this.$store.state.report.filters.selectedShifts || "";
      },
      set(value) {
        this.$store.commit("report/SET_FILTER", { key: "selectedShifts", value });
      },
    },
    search: {
      get() {
        return this.$store.state.report.filters.search;
      },
      set(value) {
        this.$store.commit("report/SET_FILTER", { key: "search", value });
      },
    },
    topupSource: {
      get() {
        return this.$store.state.report.filters.topupSource || [];
      },
      set(value) {
        this.$store.commit("report/SET_FILTER", { key: "topupSource", value });
      },
    },
    paymentMethod: {
      get() {
        return this.$store.state.report.filters.paymentMethod || [];
      },
      set(value) {
        this.$store.commit("report/SET_FILTER", { key: "paymentMethod", value });
      },
    },
  },
  methods: {
    /** Izbor u jednom redu: sve ("Sve · 3"), naziv jedne stavke ili "izabrano/ukupno". */
    pickedSummary(items, ids) {
      const picked = items.filter((item) => ids.includes(item.id));
      if (!picked.length) {
        return "";
      }
      if (picked.length === items.length) {
        return `${this.$t("report_mobile.all")} · ${items.length}`;
      }
      if (picked.length === 1) {
        return this.toLatin(picked[0].name || "");
      }
      return `${picked.length}/${items.length}`;
    },
    handleMobileFilterUpdate({ key, value }) {
      if (key === "dates") {
        this.dates = value;
      } else if (key === "restaurants") {
        if (this.activeTab === 8) {
          this.selectedRestaurant = value;
        } else {
          this.selectedRestaurants = value;
        }
      } else if (key === "branches") {
        this.selectedBranches = value;
      } else if (key === "shifts") {
        this.selectedShifts = value;
      } else if (key === "search") {
        this.search = value;
      } else if (key === "topupSource") {
        this.topupSource = value;
      } else if (key === "paymentMethod") {
        this.paymentMethod = value;
      }
    },
    onFiltersApply() {
      // Filters are already applied via v-model/setters
    },
    resetFilters() {
      const today = new Date();
      const from = new Date(today);
      const to = new Date(today);
      this.dates = [from, to];
      this.selectedRestaurants = this.restaurants.map((r) => r.id);
      this.selectedRestaurant =
        this.restaurants.length > 0 ? this.restaurants[0].id : null;
      this.selectedShifts = [];
      this.selectedBranches = [];
      this.search = "";
      this.topupSource = ["Firma", "Radnik"];
      this.paymentMethod = ["Automatska", "Kartica", "Gotovina", "Bankarski prenos"];
    },
    handleNavigateToTopup(userId) {
      this.preselectedUserId = userId;
      this.activeTab = 5; // Topup tab
    },
  },
};
</script>
